import {
  LinkedComponent,
  ReferenceDirection,
  StartSetExpansion,
  PARENT_REF_TYPE_NAME,
} from '@ardoq/api-types';
import { anyType } from '@ardoq/global-consts';
import { withoutStartSetExpansion } from './withoutStartSetExpansion';
import { ArdoqIconCategory } from '@ardoq/data-model';
import { IconName } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { RawGraphNode, TraversalBuilderState } from '../types';
import { componentTypeHierarchyOperations } from 'streams/componentTypeHierarchies/componentTypeHierarchyOperations';
import { setStartTypeAndTraversalStartSetOrCount } from './startContext';
import { updateTripleOptions } from './setTraversal';
import { updateEdgesToBeRemovedOnCollapsing } from './pathCollapsing';
import { toggleTripleOption } from './toggleTripleOption';
import { getRepresentationDataWithColorVariants } from 'viewpointBuilder/components/getRepresentationDataWithColorVariants';
import { COMPONENT_HIERARCHY_TYPE_LABEL } from './getComponentTypeDisplayLabel';

// Own typeId so the typeId-keyed node-style cache doesn't clash with the
// unrelated default "Any Type" wildcard (anyType stays the modelId/label).
export const HIERARCHY_NODE_TYPE_ID = '@@hierarchy-node@@';

const HIERARCHY_NODE_COLOR = colors.grey500;

// remix_ icons are SVG, so this renders via ComponentRepresentation.
const HIERARCHY_NODE_ICON = {
  id: IconName.TREE_STRUCTURE,
  label: COMPONENT_HIERARCHY_TYPE_LABEL,
  category: ArdoqIconCategory.Ardoq,
  isSVG: true,
};

/**
 * A hierarchy spanning a single component type does not need the wildcard
 * "Context component type" node: the concrete start type already lets the
 * user define traversals that apply to all of its (same-typed) descendants.
 * We only swap in the wildcard node when the hierarchy actually spans more
 * than one component type.
 */
const MIN_TYPES_FOR_WILDCARD_NODE = 2;

const buildWildcardRawGraphNode = (
  state: TraversalBuilderState,
  startComponentType: string,
  hierarchyComponentTypes: string[]
): RawGraphNode | null => {
  const startRawGraphNode = state.rawGraphNodes.get(startComponentType);
  if (!startRawGraphNode) return null;
  return {
    modelId: anyType,
    label: anyType,
    // Generic look (tree icon, grey, own typeId, tooltip) for the synthetic
    // node; label stays anyType to serialise as the wildcard endpoint.
    metaData: {
      ...startRawGraphNode.metaData,
      label: anyType,
      typeId: HIERARCHY_NODE_TYPE_ID,
      color: HIERARCHY_NODE_COLOR,
      representationData: getRepresentationDataWithColorVariants({
        isImage: false,
        value: IconName.TREE_STRUCTURE,
        icon: HIERARCHY_NODE_ICON,
        color: HIERARCHY_NODE_COLOR,
      }),
      tooltip: `Includes: ${hierarchyComponentTypes.join(', ')}`,
    },
  };
};

// JSON-encode the parts so the key is unambiguous: both a reference type label
// and a component type name can contain spaces, so a plain-string separator
// could collide across different (label, type) pairs.
const dedupeKey = (referenceLabel: string, componentId: string): string =>
  JSON.stringify([referenceLabel, componentId]);

/**
 * Collects the union of every hierarchy type's edges as edges of the wildcard
 * node: the hierarchy endpoint becomes the wildcard (the map key), the far
 * endpoint stays concrete, and (reference type, far type) duplicates collapse
 * into one option. Only parent-of edges are dropped (that recursion is shown by
 * the decorative self-reference); all other references - including those between
 * hierarchy members - are offered.
 */
const collectWildcardEdges = (
  state: TraversalBuilderState,
  hierarchyComponentTypes: string[],
  mapKey: 'sourceMap' | 'targetMap'
): LinkedComponent[] => {
  const seen = new Set<string>();
  return hierarchyComponentTypes.flatMap(componentType =>
    (state.baseGraph[mapKey].get(componentType) ?? []).filter(
      ({ referenceId, componentId }) => {
        const edge = state.rawGraphEdges.get(referenceId);
        if (!edge) return false;
        if (edge.label === PARENT_REF_TYPE_NAME) return false;
        const key = dedupeKey(edge.label, componentId);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }
    )
  );
};

// When expansion is toggled as a flag only (no wildcard-node swap, e.g. a
// single-type hierarchy), the current triple options - and crucially their
// instance-count request (`fetchCountsArgs`) - were built for the previous
// expansion flag, so the option counts would keep reflecting the previous
// (un-)expanded start set. Regenerate them so the count request matches the
// current `startSetExpansion`. (The wildcard-swap path already rebuilds the
// options via `setStartTypeAndTraversalStartSetOrCount`.)
//
// We clear the cached `instanceCountsResponse` first: `updateTripleOptions`
// otherwise reuses it, which makes the options skip the count request
// (`isFetchCounts` false) and re-apply the stale counts. Clearing it forces a
// fresh count request reflecting the current expansion flag.
const refreshOptionCounts = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  if (!state.startNode) {
    return state;
  }
  return updateTripleOptions({
    ...state,
    tripleOptions: { ...state.tripleOptions, instanceCountsResponse: null },
  });
};

// createOptions hides the "Is Parent Of" option (both directions) at the start
// node once expansion is on, so any already-selected parent-of hop would
// otherwise linger in the saved traversal and the option counts. Deselect it
// here. Runs before the flag is set so the options are still present to toggle
// off.
const deselectRedundantParentOf = (
  state: TraversalBuilderState
): TraversalBuilderState =>
  [
    ...state.tripleOptions.outgoing.map(option => ({
      option,
      direction: ReferenceDirection.OUTGOING,
    })),
    ...state.tripleOptions.incoming.map(option => ({
      option,
      direction: ReferenceDirection.INCOMING,
    })),
  ]
    .filter(
      ({ option }) =>
        option.isSelected &&
        option.namedDirectedTriple.referenceType === PARENT_REF_TYPE_NAME
    )
    .reduce(
      (accState, { option, direction }) =>
        toggleTripleOption(accState, {
          tripleId: option.tripleId,
          direction,
          namedDirectedTriple: option.namedDirectedTriple,
          isSelected: option.isSelected,
        }),
      state
    );

const enableHierarchyNodeExpansion = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  const withFlag: TraversalBuilderState = {
    ...state,
    startSetExpansion: 'descendants',
  };
  const startComponentType =
    state.startSetExpansionState?.originalStartComponentType ??
    state.startComponentType;
  const isAlreadyTransformed =
    Boolean(state.startSetExpansionState) && state.rawGraphNodes.has(anyType);
  // Already transformed, or no concrete start type / metamodel to build from:
  // just record the flag (the backend still expands same-typed descendants).
  if (
    isAlreadyTransformed ||
    !startComponentType ||
    !state.startNode ||
    !state.isMetaModelLoaded
  ) {
    return refreshOptionCounts(withFlag);
  }
  const hierarchyComponentTypes =
    componentTypeHierarchyOperations.getDescendantTypes(
      state.componentTypeHierarchies,
      startComponentType
    );
  if (hierarchyComponentTypes.length < MIN_TYPES_FOR_WILDCARD_NODE) {
    return refreshOptionCounts({
      ...deselectRedundantParentOf(state),
      startSetExpansion: 'descendants',
    });
  }

  const wildcardRawGraphNode = buildWildcardRawGraphNode(
    state,
    startComponentType,
    hierarchyComponentTypes
  );
  if (!wildcardRawGraphNode) return refreshOptionCounts(withFlag);

  const stateWithWildcardGraphData: TraversalBuilderState = {
    ...withFlag,
    startSetExpansionState: {
      originalStartComponentType: startComponentType,
      hierarchyComponentTypes,
    },
    rawGraphNodes: new Map(state.rawGraphNodes).set(
      anyType,
      wildcardRawGraphNode
    ),
    // Only `sourceMap`/`targetMap` get a wildcard entry: they are keyed by
    // component type (the node), so the synthetic `anyType` node needs its own
    // adjacency — this is what the triple-option machinery reads
    // (`baseGraph.sourceMap.get(node.modelId)` in `setTriplesOptions`).
    // `baseGraph.referenceMap` is intentionally left as-is: it is keyed by
    // reference id (the edge), not by node, and the wildcard introduces no new
    // reference entities — it only re-anchors the hierarchy types' existing
    // references onto the synthetic node, and those ids are already present.
    baseGraph: {
      ...state.baseGraph,
      sourceMap: new Map(state.baseGraph.sourceMap).set(
        anyType,
        collectWildcardEdges(state, hierarchyComponentTypes, 'sourceMap')
      ),
      targetMap: new Map(state.baseGraph.targetMap).set(
        anyType,
        collectWildcardEdges(state, hierarchyComponentTypes, 'targetMap')
      ),
    },
  };

  // Switching the start type to the wildcard sentinel rebuilds the start node,
  // clears the selected graph and regenerates the triple options through the
  // existing machinery — the wildcard node then behaves like any other node.
  return setStartTypeAndTraversalStartSetOrCount(stateWithWildcardGraphData, {
    startType: anyType,
    traversalStartSet: state.traversalStartSet ?? [],
    traversalStartQuery: state.traversalStartQuery ?? null,
  });
};

const disableHierarchyNodeExpansion = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  const { startSetExpansionState } = state;
  // `startComponentType` is left as the wildcard sentinel here so the start-type
  // switch below detects a change and rebuilds the graph for the restored
  // concrete type.
  const stripped = withoutStartSetExpansion(state);

  // Flag-only expansion (no wildcard node to restore): refresh the options so
  // their count request drops `startSetExpansion` and the counts reset to the
  // un-expanded start set, and re-resolve the collapsed edges, since dropping
  // the recursive rules leaves the graph drawing edges no rule collapses. The
  // wildcard path below goes through the start-type switch, which rebuilds both.
  if (!startSetExpansionState) {
    return updateEdgesToBeRemovedOnCollapsing(refreshOptionCounts(stripped));
  }

  return setStartTypeAndTraversalStartSetOrCount(stripped, {
    startType: startSetExpansionState.originalStartComponentType,
    traversalStartSet: state.traversalStartSet ?? [],
    traversalStartQuery: state.traversalStartQuery ?? null,
  });
};

/**
 * Applies the start-set expansion toggle. When enabled with a multi-type
 * hierarchy, the concrete start node is swapped for the synthetic
 * "Context component type" wildcard node whose triple options are the union
 * of every hierarchy type's outward references. When disabled, the original
 * concrete start node is restored. The discard of any triples added while
 * expanded is intentional for now (see PR description).
 */
export const applyStartSetExpansion = (
  state: TraversalBuilderState,
  startSetExpansion: StartSetExpansion | null
): TraversalBuilderState => {
  if (startSetExpansion === 'descendants') {
    return enableHierarchyNodeExpansion(state);
  }
  return disableHierarchyNodeExpansion(state);
};
